(() => {
  const ELEMENT_NAME = 'ifa-visit-planner';

  if (customElements.get(ELEMENT_NAME)) return;

  class IfaVisitPlanner extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized || this.connectFrame) return;

      this.connectFrame = window.requestAnimationFrame(() => {
        this.connectFrame = null;

        if (this.isConnected) {
          this.initialize();
        }
      });
    }

    initialize() {
      if (this.isInitialized) return;

      this.viewport = this.querySelector('[data-ifa-visit-viewport]');
      this.carousel = this.querySelector('.ifa-visit__carousel');
      this.cards = Array.from(this.querySelectorAll('[data-ifa-visit-card]'));
      this.previousButton = this.querySelector('[data-ifa-visit-prev]');
      this.nextButton = this.querySelector('[data-ifa-visit-next]');
      this.status = this.querySelector('[data-ifa-visit-status]');

      if (!this.viewport || !this.cards.length) return;

      this.isInitialized = true;
      this.abortController = new AbortController();
      this.mobileQuery = window.matchMedia('(max-width: 1023px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.currentIndex = this.getDefaultIndex();
      this.scrollTimer = null;
      this.layoutFrame = null;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleScrollEnd = this.handleScrollEnd.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handlePrevious = this.handlePrevious.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleLayoutChange = this.handleLayoutChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);

      const listenerOptions = { signal: this.abortController.signal };

      this.viewport.addEventListener('scroll', this.handleScroll, {
        passive: true,
        signal: this.abortController.signal
      });
      this.viewport.addEventListener('scrollend', this.handleScrollEnd, listenerOptions);
      this.viewport.addEventListener('keydown', this.handleKeydown, listenerOptions);
      this.previousButton?.addEventListener('click', this.handlePrevious, listenerOptions);
      this.nextButton?.addEventListener('click', this.handleNext, listenerOptions);
      window.addEventListener('resize', this.handleLayoutChange, listenerOptions);
      document.addEventListener('shopify:block:select', this.handleBlockSelect, listenerOptions);

      if (typeof this.mobileQuery.addEventListener === 'function') {
        this.mobileQuery.addEventListener('change', this.handleLayoutChange);
      } else {
        this.mobileQuery.addListener(this.handleLayoutChange);
      }

      this.dataset.cardCount = String(this.cards.length);
      this.syncState();
      this.scrollToCard(this.currentIndex, 'auto');
      this.scheduleAlignment();
    }

    disconnectedCallback() {
      window.cancelAnimationFrame(this.connectFrame);
      this.connectFrame = null;

      if (!this.isInitialized) return;

      this.abortController?.abort();

      if (typeof this.mobileQuery?.removeEventListener === 'function') {
        this.mobileQuery.removeEventListener('change', this.handleLayoutChange);
      } else {
        this.mobileQuery?.removeListener(this.handleLayoutChange);
      }

      window.clearTimeout(this.scrollTimer);
      window.cancelAnimationFrame(this.layoutFrame);

      this.scrollTimer = null;
      this.layoutFrame = null;
      this.isInitialized = false;
    }

    getDefaultIndex() {
      const defaultKey = this.dataset.defaultCard || 'hall_map';
      const defaultIndex = this.cards.findIndex((card) => card.dataset.cardKey === defaultKey);

      return defaultIndex >= 0 ? defaultIndex : 0;
    }

    clampIndex(index) {
      return Math.max(0, Math.min(this.cards.length - 1, index));
    }

    getClosestIndex() {
      const viewportRect = this.viewport.getBoundingClientRect();
      const viewportCenter = viewportRect.left + viewportRect.width / 2;
      let closestIndex = this.currentIndex;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.cards.forEach((card, index) => {
        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        const distance = Math.abs(cardCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    }

    getScrollBehavior(requestedBehavior = 'smooth') {
      if (requestedBehavior === 'auto' || this.reducedMotionQuery.matches) return 'auto';
      return 'smooth';
    }

    scrollToCard(index, requestedBehavior = 'smooth') {
      if (!this.mobileQuery.matches) {
        this.viewport.scrollTo({ left: 0, behavior: 'auto' });
        return;
      }

      const card = this.cards[index];
      const firstCard = this.cards[0];
      if (!card || !firstCard) return;

      const targetLeft = card.offsetLeft - firstCard.offsetLeft;
      this.viewport.scrollTo({
        left: targetLeft,
        behavior: this.getScrollBehavior(requestedBehavior)
      });
    }

    setActiveIndex(index, options = {}) {
      const previousIndex = this.currentIndex;
      const nextIndex = this.clampIndex(index);
      const shouldScroll = options.scroll !== false;
      const shouldAnnounce = options.announce === true;

      this.currentIndex = nextIndex;
      this.syncState();

      if (shouldScroll) {
        this.scrollToCard(nextIndex, options.behavior || 'smooth');
      }

      if (shouldAnnounce && previousIndex !== nextIndex) {
        this.dispatchEvent(new CustomEvent('ifa:visit-planner:change', {
          bubbles: true,
          detail: {
            index: nextIndex,
            card: this.cards[nextIndex]?.dataset.cardKey || ''
          }
        }));
      }
    }

    syncState() {
      const isMobile = this.mobileQuery.matches;

      this.cards.forEach((card, index) => {
        if (isMobile) {
          card.setAttribute('aria-roledescription', 'slide');
        } else {
          card.removeAttribute('aria-roledescription');
        }

        if (isMobile && index === this.currentIndex) {
          card.setAttribute('aria-current', 'true');
        } else {
          card.removeAttribute('aria-current');
        }
      });

      this.viewport.tabIndex = isMobile ? 0 : -1;

      if (this.carousel) {
        if (isMobile) {
          this.carousel.setAttribute('aria-roledescription', 'carousel');
        } else {
          this.carousel.removeAttribute('aria-roledescription');
        }
      }

      if (this.previousButton) {
        this.previousButton.disabled = !isMobile || this.currentIndex === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = !isMobile || this.currentIndex === this.cards.length - 1;
      }

      if (this.status) {
        const label = this.dataset.statusLabel || 'Visit card';
        this.status.textContent = isMobile ? `${label} ${this.currentIndex + 1} of ${this.cards.length}` : '';
      }

      this.dataset.activeIndex = String(this.currentIndex);
      this.dataset.activeCard = this.cards[this.currentIndex]?.dataset.cardKey || '';
    }

    handleScroll() {
      if (!this.mobileQuery.matches) return;

      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = window.setTimeout(this.handleScrollEnd, 100);
    }

    handleScrollEnd() {
      if (!this.mobileQuery.matches) return;

      window.clearTimeout(this.scrollTimer);
      this.scrollTimer = null;
      this.setActiveIndex(this.getClosestIndex(), { scroll: false, announce: true });
    }

    handleKeydown(event) {
      if (!this.mobileQuery.matches || event.altKey || event.ctrlKey || event.metaKey) return;

      const keyTargets = {
        ArrowLeft: this.currentIndex - 1,
        ArrowRight: this.currentIndex + 1,
        Home: 0,
        End: this.cards.length - 1
      };

      if (!(event.key in keyTargets)) return;

      event.preventDefault();
      this.setActiveIndex(keyTargets[event.key], { announce: true });
    }

    handlePrevious() {
      this.setActiveIndex(this.currentIndex - 1, { announce: true });
    }

    handleNext() {
      this.setActiveIndex(this.currentIndex + 1, { announce: true });
    }

    handleLayoutChange() {
      this.syncState();
      this.scheduleAlignment();
    }

    handleBlockSelect(event) {
      const selectedBlockId = event.detail?.blockId;
      if (!selectedBlockId) return;

      const selectedIndex = this.cards.findIndex((card) => card.dataset.blockId === selectedBlockId);
      if (selectedIndex < 0) return;

      this.setActiveIndex(selectedIndex, { behavior: 'auto' });
    }

    scheduleAlignment() {
      window.cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = window.requestAnimationFrame(() => {
        this.scrollToCard(this.currentIndex, 'auto');
        this.layoutFrame = null;
      });
    }
  }

  customElements.define(ELEMENT_NAME, IfaVisitPlanner);
})();
